import * as React from 'react';
import { useInstantSearch, useSearchBox } from 'react-instantsearch-hooks-web';
import styled, { keyframes } from 'styled-components';
import {
  enableBodyScroll,
  clearAllBodyScrollLocks,
  disableBodyScroll,
} from 'body-scroll-lock-upgrade';
import { useMediaQuery } from 'react-responsive';
import LatestPodcasts from './LatestPodcasts';
import SearchResults from './SearchResults';
import LatestPostsStandalone from './LatestPostsStandalone';
import LatestPlaylists from './LatestPlaylists';
import { cssClampValue } from '../utils/common';
import PopularRecordings from './PopularRecordings';
import zIndexes from '../z-indexes';
import SearchContext from '../contexts/SearchContext';
import useKeyboardEventHandler from '../hooks/useKeyboardEventHandler';

const fadeIn = keyframes`
  to {
    opacity: 1;
  }
`;

const Wrapper = styled.div<{ $isSearchOpen: boolean }>`
  position: fixed;
  top: 72px;
  left: 0;
  right: 0;
  bottom: 0;
  padding: clamp(20px, 4.5vw, 50px) 0;
  background: var(--earth-fm--color--background);
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  pointer-events: ${({ $isSearchOpen }) => ($isSearchOpen ? 'auto' : 'none')};
  opacity: ${({ $isSearchOpen }) => ($isSearchOpen ? 1 : 0)};
  transition: opacity 0.2s ease-in-out;
  z-index: ${({ $isSearchOpen }) => ($isSearchOpen ? zIndexes.headerSearchResults : -1)};
`;

const DefaultContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${cssClampValue(40, 80)};
  opacity: 0;
  animation: ${fadeIn} 0.3s linear forwards;
`;

const LatestPosts = styled(LatestPostsStandalone)`
  margin: 0;

  &__title {
    margin: 0 0 ${cssClampValue(10, 40)};
    font-family: var(--earth-fm--font-family--jakarta);
    font-size: ${cssClampValue(18, 24)};
  }
`;

export default function SearchContent() {
  const {
    query,
    isSearchOpen,
    isShowingFilters,
    setIsSearchOpen,
    setIsShowingFilters,
    setQuery,
    setIsNavigatingWithKeyboard,
  } = React.useContext(SearchContext);
  const { refine } = useSearchBox();
  const { indexUiState, setIndexUiState } = useInstantSearch();

  // update query when user types
  React.useEffect(() => {
    refine(query);
  }, [query, refine]);

  // clear filters when search is closed
  React.useEffect(() => {
    if (!isSearchOpen) {
      setIndexUiState({});
    }
  }, [isSearchOpen, setIndexUiState]);

  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const isDesktop = useMediaQuery({ minWidth: 768 });

  React.useEffect(() => {
    if (isDesktop) {
      if (wrapperRef.current) {
        enableBodyScroll(wrapperRef.current);
      }
      return undefined;
    }

    if (!wrapperRef.current) {
      clearAllBodyScrollLocks();
      return undefined;
    }

    if (isSearchOpen) {
      disableBodyScroll(wrapperRef.current);
    } else {
      enableBodyScroll(wrapperRef.current);
    }

    return () => {
      clearAllBodyScrollLocks();
    };
  }, [isDesktop, isSearchOpen]);

  const showResults = React.useMemo(
    () =>
      // show results if user has typed something or selected a filter
      isShowingFilters || Object.keys(indexUiState).length > 0,
    [indexUiState, isShowingFilters],
  );

  const handleSearchWrapperClick = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // If the user clicks on the search results, we don't want to close the search results.
      if (e.target !== e.currentTarget) {
        return;
      }

      // User might click outsize the search results to dismiss the filter dropdowns,
      // we don't want to close the search results in that case.
      if (showResults) {
        return;
      }

      // If the user clicks outside the search results, and there is no query,
      // we want to close the search results.
      setIsSearchOpen(false);
      setIsShowingFilters(false);
    },
    [setIsSearchOpen, setIsShowingFilters, showResults],
  );

  const handleKeyUp = useKeyboardEventHandler('Escape', () => {
    // if user presses escape key within search results, close the search results
    // and move focus back to search input
    if (isSearchOpen) {
      setQuery('');
      setIsSearchOpen(false);
      setIsShowingFilters(false);
      setIsNavigatingWithKeyboard(false);
      const searchInput = document.querySelector<HTMLInputElement>('#header-search-field');
      if (searchInput) {
        searchInput.focus();
      }
    }
  });

  return (
    <Wrapper
      $isSearchOpen={isSearchOpen}
      onClick={handleSearchWrapperClick}
      ref={wrapperRef}
      onKeyUp={handleKeyUp}
    >
      {isSearchOpen ? (
        <div className="container">
          {showResults ? (
            <SearchResults />
          ) : (
            <DefaultContent>
              <PopularRecordings title="Trending soundscapes right now" isDefaultContentVisible />
              <LatestPodcasts title="Listen to Earth.fm artist talk" />
              <LatestPosts />
              <LatestPlaylists title="Staff playlists" />
            </DefaultContent>
          )}
        </div>
      ) : null}
    </Wrapper>
  );
}
